import { logDebug, logWarn } from '@/utils/secureLogger'

/**
 * Age verification utility for session booking
 * Ensures students are 18+ for face-to-face sessions
 */

export interface AgeVerificationResult {
  isValid: boolean
  age: number
  message: string
  canBookFaceToFace: boolean
}

/**
 * Calculate age from date of birth string
 * @param dateOfBirth - Date of birth in format "YYYY-MM-DD" or "YYYY/MM/DD"
 * @returns Age in years
 */
export function calculateAge(dateOfBirth: string): number {
  if (!dateOfBirth) {
    logWarn('Age verification: No date of birth provided')
    return 0
  }

  try {
    // Parse the date string (handles both YYYY-MM-DD and YYYY/MM/DD formats)
    const birthDate = new Date(dateOfBirth.replace(/\//g, '-'))
    
    if (isNaN(birthDate.getTime())) {
      logWarn('Age verification: Invalid date format', { dateOfBirth })
      return 0
    }

    const today = new Date()
    let age = today.getFullYear() - birthDate.getFullYear()
    const monthDiff = today.getMonth() - birthDate.getMonth()
    
    // Adjust age if birthday hasn't occurred this year
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--
    }

    logDebug('Age verification: Calculated age', { dateOfBirth, age })
    return age
  } catch (error) {
    logWarn('Age verification: Error calculating age', { dateOfBirth, error })
    return 0
  }
}

/**
 * Verify if user can book face-to-face sessions based on age
 * @param dateOfBirth - User's date of birth
 * @param sessionMode - Session mode ('face-to-face' or 'online')
 * @returns Age verification result
 */
export function verifyAgeForSession(dateOfBirth: string, sessionMode: string): AgeVerificationResult {
  const age = calculateAge(dateOfBirth)
  const isFaceToFace = sessionMode === 'face-to-face'
  const isAgeValid = age >= 18

  let message = ''
  let canBookFaceToFace = true

  if (isFaceToFace && !isAgeValid) {
    canBookFaceToFace = false
    if (age === 0) {
      message = 'Unable to verify your age. Please ensure your date of birth is correctly set in your profile.'
    } else {
      message = `You must be 18 years or older to book face-to-face sessions.`
    }
  } else if (isFaceToFace && isAgeValid) {
    message = 'Age verification passed. You can book face-to-face sessions.'
  } else if (!isFaceToFace) {
    message = 'Online sessions are available to all students regardless of age.'
    canBookFaceToFace = true // Not relevant for online sessions
  }

  logDebug('Age verification result', { 
    dateOfBirth, 
    age, 
    sessionMode, 
    isAgeValid, 
    canBookFaceToFace,
    message 
  })

  return {
    isValid: isAgeValid || !isFaceToFace,
    age,
    message,
    canBookFaceToFace
  }
}

/**
 * Get user-friendly age restriction message
 * @param age - User's age
 * @param sessionMode - Session mode
 * @returns User-friendly message
 */
export function getAgeRestrictionMessage(age: number, sessionMode: string): string {
  if (sessionMode !== 'face-to-face') {
    return 'Online sessions are available to all students.'
  }

  if (age === 0) {
    return 'Please verify your date of birth in your profile to book face-to-face sessions.'
  }

  if (age < 18) {
    const yearsUntil18 = 18 - age
    return `Face-to-face sessions require students to be 18 years or older.`
  }

  return 'You are eligible to book face-to-face sessions.'
}

/**
 * Check if user profile has valid date of birth
 * @param userProfile - User profile object
 * @returns Whether date of birth is valid
 */
export function hasValidDateOfBirth(userProfile: any): boolean {
  if (!userProfile?.dateOfBirth) {
    return false
  }

  const age = calculateAge(userProfile.dateOfBirth)
  return age > 0
}
